import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]

import { ref, provide, onMounted, onBeforeUnmount, computed } from "vue";
import cytoscape, { EventObject } from "cytoscape";
import { CytoCore, CytoEvent, CytoscapeProps } from "@/types";
import { logStore } from "@/hooks";
import CyContextMenu from "./CyContextMenu.vue";


/**
 * A wrapper for managing a Cytoscape instance.
 */
const __default__ = {
  name: "Cytoscape",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    id: {},
    graph: {},
    class: {}
  },
  emits: ["click"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const initialized = ref(false);
const container = ref<HTMLElement | null>(null);
const instance = ref<CytoCore | undefined>(undefined);
const resolve = ref<(value: PromiseLike<CytoCore> | CytoCore) => void>(
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  () => {}
);
// eslint-disable-next-line @typescript-eslint/no-empty-function
const reject = ref<() => void>(() => {});

const relTransform = ref("");

const className = computed(
  () => `cy-container bg-background ${props.class || ""}`
);

/**
 * Initializes all plugins.
 */
function beforeCreated(core: typeof cytoscape) {
  props.graph.plugins.forEach((plugin) => {
    try {
      plugin.initialize(core);
    } catch (e) {
      logStore.onDevError(`Plugin installation error: ${e}`);
    }
  });
}

/**
 * Finalizes all plugins.
 */
async function afterCreated(core: CytoCore) {
  if (props.graph.saveCy) {
    props.graph.saveCy(core);
  } else {
    logStore.onDevError(
      `Unable to save cytoscape instance in: ${props.graph.name}`
    );
  }

  props.graph.plugins.forEach((plugin) => {
    plugin.afterInit(core);
  });

  props.graph.afterInit(core);

  initialized.value = true;
}

/**
 * Initialized cytoscape to be synchronized with this component.
 */
function initCy(): void {
  // Set the container style.
  container.value?.setAttribute("id", props.id);
  container.value?.setAttribute("width", "100%");
  container.value?.setAttribute("style", "min-height: 600px;");

  // Apply lifecycle hooks.
  beforeCreated(cytoscape);

  // Create cytoscape instance.
  const cyInstance = cytoscape({
    container: container.value,
    ...props.graph.config,
  }) as CytoCore;

  instance.value = cyInstance;

  afterCreated(cyInstance);

  // Resolve the promise with the object created.
  resolve.value(cyInstance);
}

/**
 * Adds a listener to track panning and zooming to translate where nodes are displayed.
 */
function listenForPanZoom(): void {
  const onPanZoom = (event: EventObject) => {
    const pan = event.cy.pan();
    const zoom = event.cy.zoom();

    relTransform.value = `translate(${pan.x}px,${pan.y}px) scale(${zoom})`;
  };

  instance.value?.on(CytoEvent.PAN_ZOOM, onPanZoom);
}

/**
 * Sets up all event handlers.
 */
function listenForEmits(): void {
  listenForPanZoom();

  instance.value?.on(CytoEvent.CLICK, (event) => emit("click", event));
}

provide(
  "cy",
  new Promise<CytoCore>((res, rej) => {
    resolve.value = res;
    reject.value = rej;
  })
);

provide("relTransform", relTransform);

/**
 * Initialize cytoscape and event handlers on mount.
 */
onMounted(() => {
  initCy();
  listenForEmits();
});

/**
 * Clean up event handlers on unmount.
 */
onBeforeUnmount(() => {
  instance.value?.off(CytoEvent.PAN_ZOOM);
  instance.value?.off(CytoEvent.CLICK);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: _ctx.id,
    ref_key: "container",
    ref: container,
    class: _normalizeClass(className.value)
  }, [
    (initialized.value)
      ? _renderSlot(_ctx.$slots, "default", { key: 0 })
      : _createCommentVNode("", true),
    (initialized.value)
      ? (_openBlock(), _createBlock(CyContextMenu, { key: 1 }, {
          default: _withCtx(() => [
            _renderSlot(_ctx.$slots, "context-menu")
          ]),
          _: 3
        }))
      : _createCommentVNode("", true)
  ], 10, _hoisted_1))
}
}

})